import React, { useRef } from 'react';
import { cn } from '~/lib/utils';
import { Seam } from './Seam';
import themes from '~/design-system/components/v1_5/Header/HeaderTheme.module.css';
import styles from './LayoutShell.module.css';
import { useResizablePanes, type PaneLimits, type PaneWidthChange, type PaneWidths } from './useResizablePanes';
import type { HeaderTheme, SeamId } from './theme';
import { useV15Theme } from '~/design-system/components/v1_5/theme-context';

/** The application header band. Fixed, and the shell's only hard-coded height. */
export const APP_HEADER_HEIGHT = 40;

/**
 * The seams name the pane they size, not the pair they divide, because that is what the value in
 * `aria-valuenow` actually is.
 */
const AGENT_SEAM_LABEL = 'Agent pane width';
const DIRECTORY_SEAM_LABEL = 'Directory pane width';
/** Named for what the pane IS to the person using it, not for the slot's name in this file. */
const INSPECTOR_SEAM_LABEL = 'Properties pane width';

export interface LayoutShellProps {
  theme?: HeaderTheme;

  /**
   * The application header, already built. The shell gives it a 40px slot and nothing else — the
   * header paints its own bottom hairline.
   */
  header?: React.ReactNode;
  /** The agent pane's contents. Its panel header goes in here, not in the shell. */
  agent: React.ReactNode;
  /**
   * The browser pane's panel header — the breadcrumb band. It belongs to the shell rather than to
   * either pane below it because it spans BOTH of them: it is what makes the directory's seam start
   * at the hairline instead of running the full height of the body the way the agent's does.
   */
  browserHeader?: React.ReactNode;
  directory: React.ReactNode;
  preview: React.ReactNode;
  /**
   * The Properties pane: a third fixed pane docked to the preview's RIGHT, with its own seam on its
   * left edge (2026-09-24). Its toggle lives at the far right end of the preview bar, on the edge of
   * the pane it opens, exactly as the directory's lives at the far left end of it. Its header goes
   * in here, like the agent's.
   *
   * Optional, and drawn only while `inspectorOpen` is set as well — closed is the default, so a
   * shell that never passes it (the kit workspace, the landing demos, every story before this one)
   * lays out and clamps exactly as it did before the slot existed.
   */
  inspector?: React.ReactNode;

  /**
   * Collapse is an unmount (D4). A folded pane and its seam leave the tree outright — there is no
   * rail, no stub and no mini-strip in any of the boards, and every pixel it gives up goes to the
   * preview, which is the only pane in the chain that flexes.
   */
  agentOpen?: boolean;
  directoryOpen?: boolean;
  /** Same rule as the other two, with the opposite default: the inspector is closed until asked for. */
  inspectorOpen?: boolean;
  /**
   * Which edge the directory docks to. `right` is the Cursor arrangement (Guri, 2026-08-30): the
   * chat keeps the left, the artifact keeps the middle, and the panel of things slides in from the
   * side its toggle lives on. The seam math flips with it — see `useResizablePanes`.
   */
  directorySide?: 'left' | 'right';

  /**
   * Controlled widths. Leave undefined and the shell holds them, so a story drags standalone. A change
   * says whether the user made it (a drag, an arrow key) or the window's clamp did: see `PaneWidthChange`.
   */
  agentWidth?: number;
  defaultAgentWidth?: number;
  onAgentWidthChange?: PaneWidthChange;
  directoryWidth?: number;
  defaultDirectoryWidth?: number;
  onDirectoryWidthChange?: PaneWidthChange;
  inspectorWidth?: number;
  defaultInspectorWidth?: number;
  onInspectorWidthChange?: PaneWidthChange;
  /** Widths remembered from an earlier visit, applied when they arrive: see `useResizablePanes`. */
  savedWidths?: Partial<PaneWidths> | null;
  /** Resize limits. Partial — anything omitted keeps `DEFAULT_PANE_LIMITS`. */
  limits?: Partial<PaneLimits>;

  /** Stories and visual tests only: lights one seam without a real pointer. */
  forceSeamHover?: SeamId | null;

  className?: string;
}

/**
 * Deck's three-pane application shell, v1.5 — four with the Properties pane open.
 *
 * ```
 * app header                                                          40
 * agent | seam | browser header                                        1
 *               ------------------------------------------------      36
 *       |      | directory | seam | preview   | seam | properties
 *   380     1       252        1     remainder     1       280
 * ```
 *
 * The last two columns are the `inspector` slot, and they are the only part of the diagram that is
 * absent by default. Open, the Properties pane docks to the preview's right with its seam on its
 * LEFT edge, and the preview pays for it the way it pays for the other two — its floor is counted
 * against all three. Closed, the pane and its seam are not in the tree and the row ends at the
 * preview, which is the whole of every board.
 *
 * Two things about that diagram are decisions rather than drawings. The seam is its own 1px element
 * between the panes, so 380 and 252 are the widths the panes actually get — the file draws each
 * seam as a `border-right` inside the pane before it, which makes them 379 and 251, but every label
 * the designer wrote inside the artboard counts the seams separately, and only a real element can
 * carry an 8px hit area and its own hover colour. And a collapsed pane unmounts rather than
 * shrinking to a rail, because no board anywhere draws a rail.
 *
 * The shell owns geometry and nothing else: the panes are slots, and what goes in them — the agent
 * thread, the directory list, the preview, the properties — is somebody else's component.
 */
export function LayoutShell({
  theme: themeProp,
  header,
  agent,
  browserHeader,
  directory,
  preview,
  inspector,
  agentOpen = true,
  directoryOpen = true,
  inspectorOpen = false,
  directorySide = 'left',
  agentWidth,
  defaultAgentWidth,
  onAgentWidthChange,
  directoryWidth,
  defaultDirectoryWidth,
  onDirectoryWidthChange,
  inspectorWidth,
  defaultInspectorWidth,
  onInspectorWidthChange,
  savedWidths,
  limits,
  forceSeamHover = null,
  className,
}: LayoutShellProps) {
  const theme = useV15Theme(themeProp);
  const bodyRef = useRef<HTMLDivElement>(null);
  const splitRef = useRef<HTMLDivElement>(null);

  /**
   * Open AND supplied. An open flag with nothing to draw would still take a pane's width and a seam
   * out of the preview for an empty column, so the hook is told the truth rather than the flag.
   */
  const showInspector = inspectorOpen && Boolean(inspector);

  const panes = useResizablePanes({
    agentWidth,
    defaultAgentWidth,
    onAgentWidthChange,
    directoryWidth,
    defaultDirectoryWidth,
    onDirectoryWidthChange,
    inspectorWidth,
    defaultInspectorWidth,
    onInspectorWidthChange,
    savedWidths,
    limits,
    bodyRef,
    splitRef,
    directoryOpen,
    directorySide,
    agentOpen,
    inspectorOpen: showInspector,
  });

  return (
    <div
      className={cn(themes[theme], styles.root, className)}
      data-resizing={panes.dragging !== null}
    >
      {header ? <div className={styles.headerSlot}>{header}</div> : null}

      <div ref={bodyRef} className={styles.body}>
        {agentOpen ? (
          <>
            {/* The live width, and one of the only three values in this component that cross as
                inline style: it changes on every frame of a drag, which is exactly what a custom
                property is for. It is set HERE rather than on the shell root because a custom
                property inherits — written on the root it invalidates style for the agent thread,
                the directory list and the preview on every pointermove, and at a realistic node
                count that measured ~2ms a frame thrown away for a width only this element reads. */}
            <div
              className={styles.agentPane}
              style={{ '--hv15-agent-w': `${panes.agentWidth}px` } as React.CSSProperties}
            >
              {agent}
            </div>
            {/* The agent's seam is a child of the BODY, so it runs the full height of it and
                crosses the browser header's hairline — the ┼ the boards draw at (380, 76). */}
            <Seam
              label={AGENT_SEAM_LABEL}
              theme={theme}
              forceHover={forceSeamHover === 'agent'}
              {...panes.agentSeam}
            />
          </>
        ) : null}

        <div className={styles.browserPane}>
          {browserHeader ? <div className={styles.browserHeaderSlot}>{browserHeader}</div> : null}

          <div ref={splitRef} className={styles.splitRow}>
            {directorySide === 'left' && directoryOpen ? (
              <>
                <div
                  className={styles.directoryPane}
                  style={{ '--hv15-dir-w': `${panes.directoryWidth}px` } as React.CSSProperties}
                >
                  {directory}
                </div>
                {/* The directory's seam is a child of the SPLIT ROW, so it starts BELOW the
                    header's hairline and meets it as a ┬ rather than crossing it. */}
                <Seam
                  label={DIRECTORY_SEAM_LABEL}
                  theme={theme}
                  forceHover={forceSeamHover === 'directory'}
                  {...panes.directorySeam}
                />
              </>
            ) : null}

            <div className={styles.previewPane}>{preview}</div>

            {directorySide === 'right' && directoryOpen ? (
              <>
                {/* Mirrored: seam first, pane second, and the SAME seam control — the hook flips
                    the drag's sign for this side, not this file. */}
                <Seam
                  label={DIRECTORY_SEAM_LABEL}
                  theme={theme}
                  forceHover={forceSeamHover === 'directory'}
                  {...panes.directorySeam}
                />
                <div
                  className={styles.directoryPane}
                  style={{ '--hv15-dir-w': `${panes.directoryWidth}px` } as React.CSSProperties}
                >
                  {directory}
                </div>
              </>
            ) : null}

            {showInspector ? (
              <>
                {/* Last in the row, after a right-docked directory too: the Properties pane is the
                    one on the window's edge, so its toggle at the far right of the preview bar sits
                    on the edge of the thing it opens. Seam first, pane second, like the right dock
                    above — and the hook flips this seam's drag the same way. A child of the split
                    row, so it meets the header's hairline as a ┬ like the directory's does. */}
                <Seam
                  label={INSPECTOR_SEAM_LABEL}
                  theme={theme}
                  forceHover={forceSeamHover === 'inspector'}
                  {...panes.inspectorSeam}
                />
                <div
                  className={styles.inspectorPane}
                  style={{ '--hv15-insp-w': `${panes.inspectorWidth}px` } as React.CSSProperties}
                >
                  {inspector}
                </div>
              </>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}
